import React, {createContext, useCallback, useContext} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useIsMutating} from '@tanstack/react-query';
import {ISlide} from '../../incase-domain/slide';
import useSaveSlide, {UPDATE_SLIDE_KEY} from '../../utils/hooks/slideUpdates/useSaveSlide';
import {useOnSlideModified} from '../../utils/hooks/slideUpdates/useOnSlideModified';
import {selectedSlideIndexSelector} from '../../store/selectors/slide.selectors';
import {setIsSlideModifiedDuringUpdateAction, updateSelectedSlideAction} from '../../store/actions/slide';

type mutateOptions = Parameters<ReturnType<typeof useSaveSlide>['mutate']>[1];
type SetSlideUpdateValue = {
    setSlide: (slide: ISlide, options?: mutateOptions) => void;
    setAndSaveSlide: (slide: ISlide, options?: mutateOptions) => void;
    cancelAutosave: () => void;
} | null;
const SetSlideUpdateContext = createContext<SetSlideUpdateValue>(null);

export function SetSlideUpdateProvider({children}: {children: React.ReactNode}): JSX.Element {
    const dispatch = useDispatch();
    const selectedSlideIndex = useSelector(selectedSlideIndexSelector);
    const isSlideUpdating = useIsMutating(UPDATE_SLIDE_KEY);
    const slideMutation = useSaveSlide();

    const {cancelAutosave, suppressAutosave, allowAutosave} = useOnSlideModified();

    const applySlideUpdate = useCallback(
        (slide: ISlide): void => {
            dispatch(updateSelectedSlideAction(slide, selectedSlideIndex, true));
            if (isSlideUpdating) {
                dispatch(setIsSlideModifiedDuringUpdateAction(true));
            }
        },
        [dispatch, isSlideUpdating, selectedSlideIndex]
    );

    const setSlide = useCallback(
        (slide: ISlide): void => {
            allowAutosave();
            applySlideUpdate(slide);
        },
        [allowAutosave, applySlideUpdate]
    );

    const setAndSaveSlide = useCallback(
        (slide: ISlide, options?: mutateOptions): void => {
            suppressAutosave();
            applySlideUpdate(slide);
            slideMutation.mutate({slide}, options);
        },
        [applySlideUpdate, slideMutation, suppressAutosave]
    );

    const contextValue = React.useMemo(() => ({setSlide, setAndSaveSlide, cancelAutosave}), [cancelAutosave, setSlide, setAndSaveSlide]);
    return <SetSlideUpdateContext.Provider value={contextValue}>{children}</SetSlideUpdateContext.Provider>;
}

export function useSlideUpdateContext(): NonNullable<SetSlideUpdateValue> {
    const ctx = useContext(SetSlideUpdateContext);
    if (!ctx) {
        throw new Error('useSlideUpdateContext must be used within SetSlideUpdateProvider');
    }
    return {
        setSlide: ctx.setSlide,
        setAndSaveSlide: ctx.setAndSaveSlide,
        cancelAutosave: ctx.cancelAutosave,
    };
}
